import { FrontendConstants } from '../common/FrontendConstants';
import { HttpError } from '../common/HttpError';
import { Settings } from '../common/Settings';
import { AuthClient } from '../exports';
import { CommonConstants } from '@shared/common';
import type { IAdminTenantNumberDTO, IBusinessProfileDTO, IIntegrationDTO, IOnboardingStatusDTO, IProvisionPrepareDTO, ITenantDTO, ITenantUserDTO } from '@shared/common';

/** A tenant's connected integrations together with the id of the single calendar bookings are written to (operator console view). */
export interface IAdminTenantIntegrationsResult {
  integrations: IIntegrationDTO[];
  activeSchedulingIntegrationID: string | null;
}

/** Self-profile update payload for the authenticated tenant user. */
export interface IUpdateTenantUserRequest {
  displayName?: string;
  phoneE164?: string;
  email?: string;
}

/** Business-settings update payload for the authenticated owner's tenant. */
export interface IUpdateTenantRequest {
  name?: string;
  niche?: string;
  timezone?: string;
  defaultLocale?: string;
}

/** Onboarding account-step payload: creates the tenant, owner, and business profile for the authenticated account. No provider call happens here. */
export interface IOnboardingAccountRequest {
  businessName: string;
  niche: string;
  planCode: string;
  displayName?: string;
  phoneE164?: string;
  countryCode?: string;
  timezone?: string;
  legalName?: string;
}

/**
 * Onboarding phone-step payload: the optional preferred area-code CSV hint and, on the Twilio picker path, the owner's ordered preferred numbers for the tenant's AI Receptionist Phone Number (AI
 * Number). Billing is re-verified server-side regardless of the body.
 */
export interface IProvisionPhoneRequest {
  areaCodes?: string;
  selectedNumbers?: string[];
}

/**
 * Client for tenant identity, team membership, business profile, and cross-tenant admin listing.
 *
 * - Tenant-facing reads/writes route through the user service (`tenant/...`).
 * - Business profile lives on the lobby service dashboard surface.
 * - Cross-tenant admin reads (Portal) route through the lobby service operator surface (`admin/tenants`).
 */
export class TenantClient {
  /** Singleton instance. */
  private static instance: TenantClient;
  /** Prevent external instantiation. */
  private constructor() {}

  /** @returns The singleton TenantClient instance. */
  public static GetInstance(): TenantClient {
    return TenantClient.instance ?? (TenantClient.instance = new TenantClient());
  }

  /**
   * Parses a response body that may legitimately be empty into a nullable value. A handler that returns `null` (e.g. an account with no tenant user yet) is serialized by NestJS as a 200 with an
   * empty body, so calling `res.json()` directly would throw "Unexpected end of JSON input". An empty body resolves to `null`; any non-empty body is parsed as JSON.
   *
   * @param res - The fetch response whose body may be empty.
   * @returns The parsed value, or null when the body is empty.
   */
  private static async parseNullableJson<T>(res: Response): Promise<T | null> {
    const body = (await res.text()).trim();
    return body.length === 0 ? null : (JSON.parse(body) as T);
  }

  /**
   * GET /api/user/tenant/me - Resolve the authenticated tenant user. Returns null when the authenticated account has no tenant user yet (onboarding not started).
   *
   * @param signal - AbortSignal to cancel the request if needed.
   * @returns The authenticated tenant user, or null if the account has no tenant.
   */
  public async GetMe(signal: AbortSignal): Promise<ITenantUserDTO | null> {
    const auth = await AuthClient.GetAuthClientAdapter();
    const res = await AuthClient.Fetch(Settings.SERVICE_USER_URI, 'tenant/me', { signal }, auth);
    if (!res.ok) throw new HttpError(res.status, 'Failed to fetch tenant user');
    return await TenantClient.parseNullableJson<ITenantUserDTO>(res);
  }

  /**
   * GET /api/user/tenant/onboarding/status - Resolve the account's onboarding progress so the dashboard gate can resume at the exact step the owner left off (fail-closed until complete).
   *
   * @param signal - AbortSignal to cancel the request if needed.
   * @returns The onboarding status (step, complete, tenantID).
   */
  public async GetOnboardingStatus(signal: AbortSignal): Promise<IOnboardingStatusDTO> {
    const auth = await AuthClient.GetAuthClientAdapter();
    const res = await AuthClient.Fetch(Settings.SERVICE_USER_URI, 'tenant/onboarding/status', { signal }, auth);
    if (!res.ok) throw new HttpError(res.status, 'Failed to resolve onboarding status');
    return await res.json();
  }

  /**
   * POST /api/user/tenant/onboarding/account - Onboarding step 1: create our own data (tenant, owner, profile). Re-entrant: returns the current progress unchanged if the account already exists.
   *
   * @param signal - AbortSignal to cancel the request if needed.
   * @param body - The account-step payload.
   * @returns The updated onboarding status (billing step on success).
   */
  public async ProvisionAccount(signal: AbortSignal, body: IOnboardingAccountRequest): Promise<IOnboardingStatusDTO> {
    const auth = await AuthClient.GetAuthClientAdapter();
    const res = await AuthClient.Fetch(
      Settings.SERVICE_USER_URI,
      'tenant/onboarding/account',
      {
        method: FrontendConstants.MethodPost,
        headers: { [FrontendConstants.HeaderContentType]: FrontendConstants.ContentTypeJson },
        body: JSON.stringify(body),
        signal
      },
      auth
    );
    if (!res.ok) throw new HttpError(res.status, 'Failed to create onboarding account');
    return await res.json();
  }

  /**
   * POST /api/user/tenant/onboarding/phone/prepare - Onboarding step 3, phase 1: search the Twilio candidates the owner will choose the AI Number from.
   *
   * A `select` result carries the Twilio candidates the owner must choose preferred numbers from; onboarding stays on the phone step until {@link ProvisionPhone} is called with the selection.
   * Searching for candidates allocates nothing. A `provisioned` result means onboarding is already complete (idempotent short-circuit: prior completion, or a number from an earlier attempt was
   * reused) - the returned `status` is terminal.
   *
   * @param signal - AbortSignal to cancel the request if needed.
   * @param body - The optional preferred area-code CSV hint for the AI Number.
   * @returns Either the Twilio candidates to choose from (select) or the completed status (provisioned).
   */
  public async PreparePhone(signal: AbortSignal, body: IProvisionPhoneRequest): Promise<IProvisionPrepareDTO> {
    const auth = await AuthClient.GetAuthClientAdapter();
    const res = await AuthClient.Fetch(
      Settings.SERVICE_USER_URI,
      'tenant/onboarding/phone/prepare',
      {
        method: FrontendConstants.MethodPost,
        headers: { [FrontendConstants.HeaderContentType]: FrontendConstants.ContentTypeJson },
        body: JSON.stringify(body),
        signal
      },
      auth
    );
    if (!res.ok) throw new HttpError(res.status, 'Failed to prepare phone number');
    return await res.json();
  }

  /**
   * POST /api/user/tenant/onboarding/phone - Onboarding step 3: verify billing server-side, allocate the tenant's AI Receptionist Phone Number (AI Number), and mark onboarding complete.
   *
   * @param signal - AbortSignal to cancel the request if needed.
   * @param body - The optional preferred area-code CSV hint and, on the Twilio picker path, the owner's ordered preferred numbers.
   * @returns The updated onboarding status (complete on success).
   */
  public async ProvisionPhone(signal: AbortSignal, body: IProvisionPhoneRequest): Promise<IOnboardingStatusDTO> {
    const auth = await AuthClient.GetAuthClientAdapter();
    const res = await AuthClient.Fetch(
      Settings.SERVICE_USER_URI,
      'tenant/onboarding/phone',
      {
        method: FrontendConstants.MethodPost,
        headers: { [FrontendConstants.HeaderContentType]: FrontendConstants.ContentTypeJson },
        body: JSON.stringify(body),
        signal
      },
      auth
    );
    if (!res.ok) throw new HttpError(res.status, 'Failed to provision phone number');
    return await res.json();
  }

  /**
   * PUT /api/user/tenant/me - Update the authenticated tenant user's profile.
   *
   * @param signal - AbortSignal to cancel the request if needed.
   * @param body - The fields to update.
   * @returns The updated tenant user.
   */
  public async UpdateMe(signal: AbortSignal, body: IUpdateTenantUserRequest): Promise<ITenantUserDTO> {
    const auth = await AuthClient.GetAuthClientAdapter();
    const res = await AuthClient.Fetch(
      Settings.SERVICE_USER_URI,
      'tenant/me',
      {
        method: FrontendConstants.MethodPut,
        headers: { [FrontendConstants.HeaderContentType]: FrontendConstants.ContentTypeJson },
        body: JSON.stringify(body),
        signal
      },
      auth
    );
    if (!res.ok) throw new Error('Failed to update tenant user');
    return await res.json();
  }

  /**
   * GET /api/user/tenant/mine - Resolve the authenticated owner's tenant (business settings).
   *
   * @param signal - AbortSignal to cancel the request if needed.
   * @returns The owner's tenant.
   */
  public async GetMyTenant(signal: AbortSignal): Promise<ITenantDTO> {
    const auth = await AuthClient.GetAuthClientAdapter();
    const res = await AuthClient.Fetch(Settings.SERVICE_USER_URI, 'tenant/mine', { signal }, auth);
    if (!res.ok) throw new HttpError(res.status, 'Failed to fetch tenant');
    return await res.json();
  }

  /**
   * GET /api/user/tenant/number - Resolve the authenticated owner's AI Receptionist Phone Number (AI Number), the dedicated line the tenant's clients call.
   *
   * @param signal - AbortSignal to cancel the request if needed.
   * @returns The tenant's AI Number in E.164 form, or null when none is provisioned yet.
   */
  public async GetMyNumber(signal: AbortSignal): Promise<{ e164: string | null }> {
    const auth = await AuthClient.GetAuthClientAdapter();
    const res = await AuthClient.Fetch(Settings.SERVICE_USER_URI, 'tenant/number', { signal }, auth);
    if (!res.ok) throw new HttpError(res.status, 'Failed to fetch AI number');
    return await res.json();
  }

  /**
   * PUT /api/user/tenant/mine - Update the authenticated owner's tenant business settings.
   *
   * @param signal - AbortSignal to cancel the request if needed.
   * @param body - The business-settings fields to update.
   * @returns The updated tenant.
   */
  public async UpdateMyTenant(signal: AbortSignal, body: IUpdateTenantRequest): Promise<ITenantDTO> {
    const auth = await AuthClient.GetAuthClientAdapter();
    const res = await AuthClient.Fetch(
      Settings.SERVICE_USER_URI,
      'tenant/mine',
      {
        method: FrontendConstants.MethodPut,
        headers: { [FrontendConstants.HeaderContentType]: FrontendConstants.ContentTypeJson },
        body: JSON.stringify(body),
        signal
      },
      auth
    );
    // Surface the server's reason (e.g. a field failing validation) so the dashboard can show it instead of a generic failure.
    if (!res.ok) {
      const detail = (await res.json().catch(() => null)) as { message?: unknown } | null;
      throw new Error(typeof detail?.message === 'string' ? detail.message : 'Failed to update tenant');
    }
    return await res.json();
  }

  /**
   * GET /api/user/tenant/:tenantID/users - List members of a tenant.
   *
   * @param signal - AbortSignal to cancel the request if needed.
   * @param tenantID - The tenant whose members to list.
   * @returns The tenant's users.
   */
  public async ListUsers(signal: AbortSignal, tenantID: string): Promise<ITenantUserDTO[]> {
    const auth = await AuthClient.GetAuthClientAdapter();
    const res = await AuthClient.Fetch(Settings.SERVICE_USER_URI, `tenant/${tenantID}/users`, { signal }, auth);
    if (!res.ok) throw new Error('Failed to fetch tenant users');
    return await res.json();
  }

  /**
   * GET /api/lobby/dashboard/:tenantID/profile - Fetch a tenant's business profile.
   *
   * @param signal - AbortSignal to cancel the request if needed.
   * @param tenantID - The owning tenant.
   * @param lang - Optional viewer language (BCP-47); when set, the response includes `customLabelsDisplay` with the tenant's custom Leads/Bookings labels translated into this language.
   * @returns The business profile, or null when none exists yet.
   */
  public async GetProfile(signal: AbortSignal, tenantID: string, lang?: string): Promise<IBusinessProfileDTO | null> {
    const auth = await AuthClient.GetAuthClientAdapter();
    const query = lang ? `?${new URLSearchParams({ [CommonConstants.Params.Lang]: lang }).toString()}` : '';
    const res = await AuthClient.Fetch(Settings.SERVICE_LOBBY_URI, `dashboard/${tenantID}/profile${query}`, { signal }, auth);
    if (!res.ok) throw new Error('Failed to fetch business profile');
    return await res.json();
  }

  /**
   * PUT /api/lobby/dashboard/:tenantID/profile - Create or update a tenant's business profile.
   *
   * @param signal - AbortSignal to cancel the request if needed.
   * @param tenantID - The owning tenant.
   * @param body - The profile fields to upsert.
   * @returns The saved business profile.
   */
  public async UpsertProfile(signal: AbortSignal, tenantID: string, body: Partial<IBusinessProfileDTO>): Promise<IBusinessProfileDTO> {
    const auth = await AuthClient.GetAuthClientAdapter();
    const res = await AuthClient.Fetch(
      Settings.SERVICE_LOBBY_URI,
      `dashboard/${tenantID}/profile`,
      {
        method: FrontendConstants.MethodPut,
        headers: { [FrontendConstants.HeaderContentType]: FrontendConstants.ContentTypeJson },
        body: JSON.stringify(body),
        signal
      },
      auth
    );
    // Surface the server's reason (e.g. a field failing validation) so the dashboard can show it instead of a generic failure.
    if (!res.ok) {
      const detail = (await res.json().catch(() => null)) as { message?: unknown } | null;
      throw new Error(typeof detail?.message === 'string' ? detail.message : 'Failed to save business profile');
    }
    return await res.json();
  }

  /**
   * GET /api/lobby/admin/tenants - List tenants (Portal operator only), newest first, with optional search and pagination.
   *
   * @param signal - AbortSignal to cancel the request if needed.
   * @param offset - Number of rows to skip.
   * @param limit - Maximum number of rows.
   * @param pattern - Optional free-text search term (name/slug/niche).
   * @returns The matching tenants page.
   */
  public async ListAllTenants(signal: AbortSignal, offset: number = CommonConstants.Pagination.Offset, limit: number = CommonConstants.Pagination.Limit, pattern?: string): Promise<ITenantDTO[]> {
    const params = new URLSearchParams({ limit: limit.toString(), offset: offset.toString() });
    if (pattern) params.append('pattern', pattern);
    const auth = await AuthClient.GetAuthClientAdapter();
    const res = await AuthClient.Fetch(Settings.SERVICE_LOBBY_URI, `admin/tenants?${params}`, { signal }, auth);
    if (!res.ok) throw new Error('Failed to fetch tenants');
    return await res.json();
  }

  /**
   * GET /api/lobby/admin/tenants/count - Count tenants matching an optional search term (Portal operator only).
   *
   * @param signal - AbortSignal to cancel the request if needed.
   * @param pattern - Optional free-text search term.
   * @returns The number of matching tenants.
   */
  public async CountTenants(signal: AbortSignal, pattern?: string): Promise<number> {
    const params = new URLSearchParams();
    if (pattern) params.append('pattern', pattern);
    const query = params.toString();
    const auth = await AuthClient.GetAuthClientAdapter();
    const res = await AuthClient.Fetch(Settings.SERVICE_LOBBY_URI, `admin/tenants/count${query ? `?${query}` : ''}`, { signal }, auth);
    if (!res.ok) throw new Error('Failed to fetch tenant count');
    return await res.json();
  }

  /**
   * GET /api/lobby/admin/clear/tenants - Flush the cross-tenant tenants Redis cache (Portal operator only).
   *
   * @param signal - AbortSignal to cancel the request if needed.
   * @returns 'CLEARED' confirmation string.
   */
  public async ClearTenants(signal: AbortSignal): Promise<string> {
    const auth = await AuthClient.GetAuthClientAdapter();
    const res = await AuthClient.Fetch(Settings.SERVICE_LOBBY_URI, 'admin/clear/tenants', { signal }, auth);
    if (!res.ok) throw new Error('Failed to clear tenant cache');
    return await res.text();
  }

  /**
   * GET /api/lobby/admin/tenants/:tenantID - Fetch one tenant (Portal operator only).
   *
   * @param signal - AbortSignal to cancel the request if needed.
   * @param tenantID - The tenant to fetch.
   * @returns The tenant.
   */
  public async GetTenant(signal: AbortSignal, tenantID: string): Promise<ITenantDTO> {
    const auth = await AuthClient.GetAuthClientAdapter();
    const res = await AuthClient.Fetch(Settings.SERVICE_LOBBY_URI, `admin/tenants/${tenantID}`, { signal }, auth);
    if (!res.ok) throw new Error('Failed to fetch tenant');
    return await res.json();
  }

  /**
   * PUT /api/lobby/admin/tenants/:tenantID - Update a tenant's core business settings from the operator console (Portal operator only). Cross-tenant counterpart to {@link UpdateMyTenant}.
   *
   * @param signal - AbortSignal to cancel the request if needed.
   * @param tenantID - The tenant to update.
   * @param body - The business-settings fields to update.
   * @returns The updated tenant.
   */
  public async UpdateTenant(signal: AbortSignal, tenantID: string, body: IUpdateTenantRequest): Promise<ITenantDTO> {
    const auth = await AuthClient.GetAuthClientAdapter();
    const res = await AuthClient.Fetch(
      Settings.SERVICE_LOBBY_URI,
      `admin/tenants/${tenantID}`,
      {
        method: FrontendConstants.MethodPut,
        headers: { [FrontendConstants.HeaderContentType]: FrontendConstants.ContentTypeJson },
        body: JSON.stringify(body),
        signal
      },
      auth
    );
    if (!res.ok) throw new HttpError(res.status, 'Failed to update tenant');
    return await res.json();
  }

  /**
   * PUT /api/lobby/admin/tenants/:tenantID/profile - Create or update a tenant's business profile from the operator console (Portal operator only). Cross-tenant counterpart to
   * {@link UpsertProfile}, which is gated to tenant members.
   *
   * @param signal - AbortSignal to cancel the request if needed.
   * @param tenantID - The tenant whose profile to upsert.
   * @param body - The profile fields to upsert.
   * @returns The saved business profile.
   */
  public async UpsertTenantProfile(signal: AbortSignal, tenantID: string, body: Partial<IBusinessProfileDTO>): Promise<IBusinessProfileDTO> {
    const auth = await AuthClient.GetAuthClientAdapter();
    const res = await AuthClient.Fetch(
      Settings.SERVICE_LOBBY_URI,
      `admin/tenants/${tenantID}/profile`,
      {
        method: FrontendConstants.MethodPut,
        headers: { [FrontendConstants.HeaderContentType]: FrontendConstants.ContentTypeJson },
        body: JSON.stringify(body),
        signal
      },
      auth
    );
    if (!res.ok) throw new HttpError(res.status, 'Failed to save tenant business profile');
    return await res.json();
  }

  /**
   * GET /api/lobby/admin/tenants/:tenantID/profile - Fetch one tenant's business profile (Portal operator only). Cross-tenant counterpart to {@link GetProfile}, which is gated to tenant members.
   *
   * @param signal - AbortSignal to cancel the request if needed.
   * @param tenantID - The tenant whose profile to fetch.
   * @returns The business profile, or null when none exists yet.
   */
  public async GetTenantProfile(signal: AbortSignal, tenantID: string): Promise<IBusinessProfileDTO | null> {
    const auth = await AuthClient.GetAuthClientAdapter();
    const res = await AuthClient.Fetch(Settings.SERVICE_LOBBY_URI, `admin/tenants/${tenantID}/profile`, { signal }, auth);
    if (!res.ok) throw new Error('Failed to fetch tenant business profile');
    return await res.json();
  }

  /**
   * GET /api/lobby/admin/tenants/:tenantID/integrations - Fetch one tenant's connected integrations and active scheduling pointer (Portal operator only). Lets the Tenant Details page name the
   * connected calendar instead of showing a bare integration id.
   *
   * @param signal - AbortSignal to cancel the request if needed.
   * @param tenantID - The tenant whose integrations to fetch.
   * @returns The tenant's integrations and the id of the calendar bookings are written to.
   */
  public async GetTenantIntegrations(signal: AbortSignal, tenantID: string): Promise<IAdminTenantIntegrationsResult> {
    const auth = await AuthClient.GetAuthClientAdapter();
    const res = await AuthClient.Fetch(Settings.SERVICE_LOBBY_URI, `admin/tenants/${tenantID}/integrations`, { signal }, auth);
    if (!res.ok) throw new Error('Failed to fetch tenant integrations');
    return await res.json();
  }

  /**
   * GET /api/lobby/admin/tenants/:tenantID/number - Fetch one tenant's AI Receptionist Phone Number (Portal operator only).
   *
   * @param signal - AbortSignal to cancel the request if needed.
   * @param tenantID - The tenant whose AI Number to fetch.
   * @returns The tenant's AI Number in E.164 form, or null when none is provisioned yet.
   */
  public async GetTenantNumber(signal: AbortSignal, tenantID: string): Promise<{ e164: string | null }> {
    const auth = await AuthClient.GetAuthClientAdapter();
    const res = await AuthClient.Fetch(Settings.SERVICE_LOBBY_URI, `admin/tenants/${tenantID}/number`, { signal }, auth);
    if (!res.ok) throw new Error('Failed to fetch tenant AI number');
    return await res.json();
  }

  /**
   * GET /api/lobby/admin/tenants/:tenantID/numbers - List ALL phone numbers connected to a tenant, active first (Portal operator only). The active number is the live AI line; released numbers
   * are historical.
   *
   * @param signal - AbortSignal to cancel the request if needed.
   * @param tenantID - The tenant whose numbers to list.
   * @returns The tenant's numbers, active first.
   */
  public async GetTenantNumbers(signal: AbortSignal, tenantID: string): Promise<{ numbers: IAdminTenantNumberDTO[] }> {
    const auth = await AuthClient.GetAuthClientAdapter();
    const res = await AuthClient.Fetch(Settings.SERVICE_LOBBY_URI, `admin/tenants/${tenantID}/numbers`, { signal }, auth);
    if (!res.ok) throw new Error('Failed to fetch tenant numbers');
    return await res.json();
  }

  /**
   * POST /api/lobby/admin/tenants/:tenantID/number/reprovision - Release a tenant's current AI Number(s) and assign a brand-new one (Portal operator only). Irreversible: the old number stops
   * routing to the tenant.
   *
   * @param signal - AbortSignal to cancel the request if needed.
   * @param tenantID - The tenant to reprovision.
   * @param body - The optional preferred area-code CSV hint for the new number.
   * @returns The tenant's numbers after reprovisioning, active first.
   */
  public async ReprovisionTenantNumber(signal: AbortSignal, tenantID: string, body: IProvisionPhoneRequest): Promise<{ numbers: IAdminTenantNumberDTO[] }> {
    const auth = await AuthClient.GetAuthClientAdapter();
    const res = await AuthClient.Fetch(
      Settings.SERVICE_LOBBY_URI,
      `admin/tenants/${tenantID}/number/reprovision`,
      {
        method: FrontendConstants.MethodPost,
        headers: { [FrontendConstants.HeaderContentType]: FrontendConstants.ContentTypeJson },
        body: JSON.stringify(body),
        signal
      },
      auth
    );
    if (!res.ok) throw new HttpError(res.status, 'Failed to reprovision tenant number');
    return await res.json();
  }

  /**
   * DELETE /api/lobby/admin/tenants/:tenantID - Permanently delete a tenant and all of its data across every store (Portal operator admin only). Irreversible.
   *
   * @param signal - AbortSignal to cancel the request if needed.
   * @param tenantID - The tenant to delete.
   */
  public async DeleteTenant(signal: AbortSignal, tenantID: string): Promise<void> {
    const auth = await AuthClient.GetAuthClientAdapter();
    const res = await AuthClient.Fetch(Settings.SERVICE_LOBBY_URI, `admin/tenants/${tenantID}`, { method: FrontendConstants.MethodDelete, signal }, auth);
    if (!res.ok) throw new HttpError(res.status, 'Failed to delete tenant');
  }

  /**
   * PUT /api/lobby/admin/tenants/:tenantID/concurrent-limit - Override a tenant's concurrent-call cap (Portal operator only). A subsequent subscribe/plan switch reapplies the plan cap and
   * overwrites this value.
   *
   * @param signal - AbortSignal to cancel the request if needed.
   * @param tenantID - The tenant to update.
   * @param concurrentLimit - The new concurrent-call cap.
   * @returns The updated tenant record.
   */
  public async SetTenantConcurrentLimit(signal: AbortSignal, tenantID: string, concurrentLimit: number): Promise<ITenantDTO> {
    const auth = await AuthClient.GetAuthClientAdapter();
    const res = await AuthClient.Fetch(
      Settings.SERVICE_LOBBY_URI,
      `admin/tenants/${tenantID}/concurrent-limit`,
      {
        method: FrontendConstants.MethodPut,
        headers: { [FrontendConstants.HeaderContentType]: FrontendConstants.ContentTypeJson },
        body: JSON.stringify({ concurrentLimit }),
        signal
      },
      auth
    );
    if (!res.ok) throw new HttpError(res.status, 'Failed to update concurrent limit');
    return await res.json();
  }
}
